/**
 * Acciones de gestión de citas (Fase 4a · US1) sobre el calendario de lectura.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSoloLectura } from '../auth'
import { api, ErrorApi } from '../lib/api'

interface Props {
    accountId: string
    dia: string
    profesionales: { id: number; nombre: string; servicioIds: number[] }[]
    servicios: { id: number; nombre: string; duracionMin: number | null }[]
}

export function aEaDateTime(dia: string, hhmm: string): string {
    const [h, m] = hhmm.split(':')
    return `${dia} ${h!.padStart(2, '0')}:${(m ?? '00').padStart(2, '0')}:00`
}

export function sumarMinutosEa(inicio: string, minutos: number): string {
    const [fecha, hora] = inicio.split(' ')
    const [hh, mm, ss] = (hora ?? '00:00:00').split(':').map(Number)
    const d = new Date(`${fecha}T00:00:00Z`)
    d.setUTCHours(hh ?? 0, (mm ?? 0) + minutos, ss ?? 0, 0)
    const p = (n: number) => String(n).padStart(2, '0')
    return `${fecha} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`
}

/** PATCH mover cita conservando duración. */
export function patchMoverCita(
    accountId: string,
    citaId: number,
    dia: string,
    hhmm: string,
    minutos: number,
    /**
     * A quién pasa la cita. `undefined` = no se toca.
     *
     * Desde la 015 se puede mover una cita DE PROFESIONAL arrastrándola a otra
     * columna, no solo de hora. El servidor comprueba el hueco contra el horario
     * del profesional DESTINO, que es lo que hace falta: un hueco libre para
     * Elena puede estar fuera de la jornada de Pablo.
     */
    profesionalId?: number | null,
) {
    const inicio = aEaDateTime(dia, hhmm)
    return api.patch(`/business/${accountId}/agenda/citas/${citaId}`, {
        inicio,
        fin: sumarMinutosEa(inicio, minutos),
        ...(profesionalId !== undefined && profesionalId !== null ? { profesionalId } : {}),
    })
}

export function FormularioNuevaCita({ accountId, dia, profesionales, servicios }: Props) {
    const qc = useQueryClient()
    const [abierto, setAbierto] = useState(false)
    const [hora, setHora] = useState('10:00')
    const [servicioId, setServicioId] = useState(String(servicios[0]?.id ?? ''))
    const [profesionalId, setProfesionalId] = useState(String(profesionales[0]?.id ?? ''))
    const [nombre, setNombre] = useState('')
    const [apellidos, setApellidos] = useState('')
    const [telefono, setTelefono] = useState('')
    const [email, setEmail] = useState('')
    const [error, setError] = useState<string | null>(null)

    const mut = useMutation({
        mutationFn: () =>
            api.post(`/business/${accountId}/agenda/citas`, {
                servicioId: Number(servicioId),
                profesionalId: Number(profesionalId),
                inicio: aEaDateTime(dia, hora),
                cliente: {
                    nombre,
                    apellidos: apellidos || undefined,
                    telefono: telefono || undefined,
                    email: email || undefined,
                },
            }),
        onSuccess: async () => {
            setError(null)
            setAbierto(false)
            setNombre('')
            setApellidos('')
            setTelefono('')
            await qc.invalidateQueries({ queryKey: ['calendario', accountId] })
        },
        onError: (e) => {
            if (!(e instanceof ErrorApi)) {
                setError('No se pudo crear la cita')
                return
            }
            const motivo = e.message
            if (motivo === 'fuera_horario') {
                setError('Fuera del horario del profesional (o en vacaciones).')
                return
            }
            if (motivo === 'solape') {
                setError('Esa franja ya está ocupada para este profesional.')
                return
            }
            setError(`No se pudo crear: ${motivo}${e.status ? ` (${e.status})` : ''}`)
        },
    })

    if (!abierto) {
        return (
            <button
                type="button"
                data-testid="nueva-cita"
                onClick={() => setAbierto(true)}
                className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1 text-xs text-sky-200 hover:bg-sky-500/20"
            >
                + Nueva cita
            </button>
        )
    }

    return (
        <form
            data-testid="form-nueva-cita"
            className="space-y-2 rounded-lg border border-neutral-800 bg-neutral-900/60 p-3"
            onSubmit={(e) => {
                e.preventDefault()
                mut.mutate()
            }}
        >
            <p className="text-xs font-medium text-neutral-200">Nueva cita · {dia}</p>
            <div className="flex flex-wrap gap-2">
                <input
                    type="time"
                    step={1800}
                    value={hora}
                    onChange={(e) => setHora(e.target.value)}
                    className="rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs text-neutral-200"
                    required
                />
                <select
                    value={servicioId}
                    onChange={(e) => setServicioId(e.target.value)}
                    className="rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs"
                    required
                >
                    {servicios.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.nombre}
                        </option>
                    ))}
                </select>
                <select
                    value={profesionalId}
                    onChange={(e) => setProfesionalId(e.target.value)}
                    className="rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs"
                    required
                >
                    {profesionales.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.nombre}
                        </option>
                    ))}
                </select>
                <input
                    placeholder="Nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs"
                    required
                />
                <input
                    placeholder="Apellidos"
                    value={apellidos}
                    onChange={(e) => setApellidos(e.target.value)}
                    className="rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs"
                />
                <input
                    placeholder="Teléfono"
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    className="rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs"
                />
                <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded border border-neutral-800 bg-neutral-950 px-2 py-1 text-xs"
                />
            </div>
            {error && <p className="text-xs text-rose-400">{error}</p>}
            <div className="flex gap-2">
                <button
                    type="submit"
                    disabled={mut.isPending}
                    className="rounded bg-sky-600 px-3 py-1 text-xs text-white disabled:opacity-50"
                >
                    {mut.isPending ? 'Guardando…' : 'Crear'}
                </button>
                <button
                    type="button"
                    onClick={() => setAbierto(false)}
                    className="rounded border border-neutral-700 px-3 py-1 text-xs text-neutral-400"
                >
                    Cancelar
                </button>
            </div>
        </form>
    )
}

export function BotonCancelarCita({
    accountId,
    citaId,
    alHecho,
}: {
    accountId: string
    citaId: number
    alHecho?: () => void
}) {
    const soloLectura = useSoloLectura()
    const qc = useQueryClient()
    const mut = useMutation({
        mutationFn: () => api.delete<{ ok: true }>(`/business/${accountId}/agenda/citas/${citaId}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['calendario', accountId] })
            alHecho?.()
        },
    })
    if (soloLectura) return null
    return (
        <button
            type="button"
            data-testid={`cancelar-cita-${citaId}`}
            disabled={mut.isPending}
            onClick={() => {
                if (confirm('¿Cancelar esta cita en la agenda?')) mut.mutate()
            }}
            className="rounded-md border border-rose-500/40 px-3 py-1.5 text-sm text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
        >
            {mut.isPending ? 'Cancelando…' : 'Cancelar cita'}
        </button>
    )
}
